/**
 * Manager Dashboard
 * Page for managers to approve/reject requests and view team calendar
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import RequestCard from '../components/RequestCard';
import CalendarView from '../components/CalendarView';
import LoadingSpinner from '../components/LoadingSpinner';
import EmployeeForm from '../components/EmployeeForm';
import PasswordChangeForm from '../components/PasswordChangeForm';
import requestService from '../services/requestService';
import employeeService from '../services/employeeService';
import logger from '../utils/logger';
import auditService from '../services/auditService';
import settingsService from '../services/settingsService';
import { formatDate } from '../utils/dateHelpers';

const ManagerDashboard = () => {
  const { user } = useAuth();
  const { requests, employees, settings, centers, refreshAll, loading: appLoading } = useApp();
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamRequests, setTeamRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [passwordResetEmployee, setPasswordResetEmployee] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  
  useEffect(() => {
    logger.info('ManagerDashboard mounted', { userId: user?.id });
    loadTeamData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, requests, employees]);
  
  const loadTeamData = async () => {
    try {
      if (!user) return;
      
      // Get team members
      const members = await employeeService.getEmployeesByManager(user.id);
      setTeamMembers(members);
      
      // Get team requests
      const memberIds = members.map(m => m.id);
      const teamReqs = requests.filter(req => memberIds.includes(req.employeeId));
      setTeamRequests(teamReqs);
      
      // Get pending requests (including cancellation requests)
      const pending = requests.filter(
        req => req.managerId === user.id && (req.status === 'pending' || req.status === 'cancellation_requested')
      );
      setPendingRequests(pending);
    } catch (error) {
      logger.error('Error loading team data', error, { userId: user?.id });
    }
  };
  
  const handleApprove = async (request) => {
    setProcessing(true);
    try {
      logger.info('Approving request', { requestId: request.id, userId: user.id });
      
      // Validate token limit before approval
      const employee = await employeeService.getEmployeeById(request.employeeId);
      if (!employee) {
        alert('Employee not found. Cannot approve request.');
        setProcessing(false);
        return;
      }
      
      const settings = await settingsService.getSettings();
      const yearlyLimit = employee.yearlyTokens || settings.yearlyTokenLimit || 12;
      const tokensUsed = employee.tokensUsed || 0;
      const requestedDays = request.days || 0;
      
      if (tokensUsed + requestedDays > yearlyLimit) {
        alert(`Cannot approve: This would exceed the yearly token limit.\n\nEmployee: ${employee.name}\nTokens Used: ${tokensUsed}\nRequested: ${requestedDays} working days\nLimit: ${yearlyLimit}\n\nThe employee would need ${tokensUsed + requestedDays - yearlyLimit} fewer tokens to approve this request.`);
        setProcessing(false);
        return;
      }
      
      await requestService.approveRequest(request.id, user.id);
      
      // Recalculate token usage based on all approved requests (ensures accuracy)
      // This ensures only approved requests count, not rejected ones
      const allRequests = await requestService.getAllRequests();
      await employeeService.recalculateTokenUsage(request.employeeId, allRequests);
      
      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'approve',
        entityType: 'request',
        entityId: request.id,
        details: { request },
      });
      
      await refreshAll();
      logger.info('Request approved successfully', { requestId: request.id });
    } catch (error) {
      logger.error('Error approving request', error, { requestId: request.id });
      alert('Failed to approve request: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleReject = async (request) => {
    const reason = prompt('Please provide a reason for rejection (optional):');
    if (reason === null) return; // User cancelled
    
    setProcessing(true);
    try {
      logger.info('Rejecting request', { requestId: request.id, userId: user.id, reason });
      
      await requestService.rejectRequest(request.id, user.id, reason);
      
      // Recalculate token usage to ensure rejected requests don't count
      // If this request was previously approved, it will be excluded now
      const allRequests = await requestService.getAllRequests();
      await employeeService.recalculateTokenUsage(request.employeeId, allRequests);
      
      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'reject',
        entityType: 'request',
        entityId: request.id,
        details: { request, reason },
      });
      
      await refreshAll();
      logger.info('Request rejected successfully', { requestId: request.id });
    } catch (error) {
      logger.error('Error rejecting request', error, { requestId: request.id });
      alert('Failed to reject request: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleApproveCancellation = async (request) => {
    setProcessing(true);
    try {
      logger.info('Approving cancellation', { requestId: request.id, userId: user.id });
      
      await requestService.approveCancellation(request.id, user.id);
      
      // Recalculate token usage - cancelled requests don't count
      const allRequests = await requestService.getAllRequests();
      await employeeService.recalculateTokenUsage(request.employeeId, allRequests);
      
      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'approve_cancellation',
        entityType: 'request',
        entityId: request.id,
        details: { request },
      });
      
      await refreshAll();
      logger.info('Cancellation approved successfully', { requestId: request.id });
    } catch (error) {
      logger.error('Error approving cancellation', error, { requestId: request.id });
      alert('Failed to approve cancellation: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleRejectCancellation = async (request) => {
    const reason = prompt('Please provide a reason for rejecting the cancellation (optional):');
    if (reason === null) return; // User cancelled
    
    setProcessing(true);
    try {
      logger.info('Rejecting cancellation', { requestId: request.id, userId: user.id, reason });
      
      await requestService.rejectCancellation(request.id, user.id, reason);
      
      // Recalculate token usage - request goes back to original status
      const allRequests = await requestService.getAllRequests();
      await employeeService.recalculateTokenUsage(request.employeeId, allRequests);
      
      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'reject_cancellation',
        entityType: 'request',
        entityId: request.id,
        details: { request, reason },
      });
      
      await refreshAll();
      logger.info('Cancellation rejected successfully', { requestId: request.id });
    } catch (error) {
      logger.error('Error rejecting cancellation', error, { requestId: request.id });
      alert('Failed to reject cancellation: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleCreateEmployee = async (formData) => {
    setProcessing(true);
    try {
      logger.info('Creating employee', { formData, userId: user.id });
      
      // Check for duplicate email (case-insensitive)
      const duplicateEmail = employees.find(
        emp => emp.email.toLowerCase() === formData.email.toLowerCase()
      );
      if (duplicateEmail) {
        alert(`An employee with email "${duplicateEmail.email}" already exists. Email addresses must be unique (case-insensitive).`);
        setProcessing(false);
        return;
      }
      
      // Get the manager's center
      const managerData = employees.find(emp => emp.id === user.id);
      if (!managerData?.centerId) {
        alert('You must be assigned to a center to create employees.');
        setProcessing(false);
        return;
      }
      
      // Ensure the new employee is assigned to this manager and the manager's center
      const employeeData = {
        ...formData,
        managerId: user.id,
        centerId: managerData.centerId, // Always use manager's center
        role: 'employee', // Managers can only create employees
      };
      
      const newEmployee = await employeeService.createEmployee(employeeData);
      
      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'create',
        entityType: 'employee',
        entityId: newEmployee.id,
        details: employeeData,
      });
      
      await refreshAll();
      setShowEmployeeForm(false);
      logger.info('Employee created successfully', { employeeId: newEmployee.id });
      alert('Employee created successfully!');
    } catch (error) {
      logger.error('Error creating employee', error, { userId: user.id });
      alert('Failed to create employee: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleUpdateEmployee = async (formData) => {
    setProcessing(true);
    try {
      logger.info('Updating employee', { employeeId: editingEmployee.id, formData, userId: user.id });
      
      // Check for duplicate email (case-insensitive), excluding the current employee
      const duplicateEmail = employees.find(
        emp => emp.id !== editingEmployee.id && 
               emp.email.toLowerCase() === formData.email.toLowerCase()
      );
      if (duplicateEmail) {
        alert(`An employee with email "${duplicateEmail.email}" already exists. Email addresses must be unique (case-insensitive).`);
        setProcessing(false);
        return;
      }
      
      // Get the manager's center
      const managerData = employees.find(emp => emp.id === user.id);
      if (!managerData?.centerId) {
        alert('You must be assigned to a center to manage employees.');
        setProcessing(false);
        return;
      }
      
      // Ensure managerId remains set to this manager and centerId stays with manager's center
      const employeeData = {
        ...formData,
        managerId: user.id,
        centerId: managerData.centerId, // Always use manager's center
        role: 'employee', // Managers can only manage employees
      };
      
      // Remove password field if empty (don't update password on edit)
      if (!employeeData.password || employeeData.password.trim() === '') {
        delete employeeData.password;
      }
      
      const updatedEmployee = await employeeService.updateEmployee(editingEmployee.id, employeeData);
      
      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'update',
        entityType: 'employee',
        entityId: updatedEmployee.id,
        details: employeeData,
      });
      
      await refreshAll();
      setEditingEmployee(null);
      setShowEmployeeForm(false);
      logger.info('Employee updated successfully', { employeeId: updatedEmployee.id });
      alert('Employee updated successfully!');
    } catch (error) {
      logger.error('Error updating employee', error, { userId: user.id });
      alert('Failed to update employee: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm('Are you sure you want to delete this employee? This action cannot be undone.')) {
      return;
    }
    
    setProcessing(true);
    try {
      logger.info('Deleting employee', { employeeId, userId: user.id });
      
      await employeeService.deleteEmployee(employeeId);
      
      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'delete',
        entityType: 'employee',
        entityId: employeeId,
      });
      
      await refreshAll();
      logger.info('Employee deleted successfully', { employeeId });
      alert('Employee deleted successfully!');
    } catch (error) {
      logger.error('Error deleting employee', error, { userId: user.id });
      alert('Failed to delete employee: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };
  
  if (appLoading && !processing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      {processing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <LoadingSpinner size="lg" text="Processing request..." />
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-bold text-primary-500 mb-2">
            Manager Dashboard
          </h1>
          <p className="text-gray-600 text-lg">Review and manage team innovation requests</p>
          <div className="mt-4 flex items-center gap-4">
            <button
              onClick={() => setShowPasswordForm(!showPasswordForm)}
              className="px-4 py-2 text-sm font-semibold text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-all duration-200 flex items-center space-x-2 bg-white border border-primary-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>{showPasswordForm ? 'Cancel' : 'Change Password'}</span>
            </button>
          </div>
        </div>
        
        {/* Password Change Form */}
        {showPasswordForm && (
          <div className="bg-white rounded-2xl shadow-soft p-8 mb-6 border border-gray-100 animate-slide-up">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Change Your Password</h3>
            <PasswordChangeForm
              onSuccess={() => {
                setShowPasswordForm(false);
                alert('Password changed successfully!');
              }}
              onCancel={() => setShowPasswordForm(false)}
            />
          </div>
        )}
        
        {/* Tabs */}
        <div className="glass p-2">
          <nav className="flex space-x-2">
            {['overview', 'employees', 'requests', 'calendar'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-200 ${
                  activeTab === tab
                    ? 'bg-primary-500 text-white shadow-lg hover:bg-primary-600'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>
        
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
        {/* Team Overview */}
        <div className="glass p-8 mb-6 animate-slide-up">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Team Overview</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="text-center p-6 bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl border border-primary-200 hover:shadow-lg transition-all duration-200">
              <div className="text-4xl font-bold text-primary-500 mb-2">{teamMembers.length}</div>
              <div className="text-sm font-semibold text-gray-600">Team Members</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl border border-yellow-100 hover:shadow-lg transition-all duration-200">
              <div className="text-4xl font-bold bg-gradient-to-r from-yellow-500 to-amber-600 bg-clip-text text-transparent mb-2">{pendingRequests.length}</div>
              <div className="text-sm font-semibold text-gray-600">Pending Requests</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100 hover:shadow-lg transition-all duration-200">
              <div className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-2">
                {teamRequests.filter(r => r.status === 'approved').length}
              </div>
              <div className="text-sm font-semibold text-gray-600">Approved Requests</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-red-50 to-rose-50 rounded-xl border border-red-100 hover:shadow-lg transition-all duration-200">
              <div className="text-4xl font-bold bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent mb-2">
                {teamRequests.filter(r => r.status === 'rejected').length}
              </div>
              <div className="text-sm font-semibold text-gray-600">Rejected Requests</div>
            </div>
          </div>
          
          {/* Employee Token Balances */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center space-x-2">
              <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span>Employee Token Balances</span>
            </h3>
            {teamMembers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No team members assigned yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {teamMembers.map(member => {
                  const tokensUsed = member.tokensUsed || 0;
                  const yearlyTokens = member.yearlyTokens || 12;
                  const remaining = yearlyTokens - tokensUsed;
                  const percentage = yearlyTokens > 0 ? (tokensUsed / yearlyTokens) * 100 : 0;
                  
                  return (
                    <div
                      key={member.id}
                      className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-200 hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex items-start gap-3 mb-3 min-w-0">
                        <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                          {member.name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-gray-900 truncate">{member.name}</div>
                          <div className="text-xs text-gray-500 truncate">{member.email}</div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Used</span>
                          <span className="font-bold text-primary-600">{tokensUsed} / {yearlyTokens}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className={`h-2.5 rounded-full transition-all duration-300 ${
                              percentage >= 100
                                ? 'bg-red-500'
                                : percentage >= 75
                                ? 'bg-yellow-500'
                                : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Remaining</span>
                          <span className={`font-semibold ${
                            remaining <= 0
                              ? 'text-red-600'
                              : remaining <= 3
                              ? 'text-yellow-600'
                              : 'text-green-600'
                          }`}>
                            {remaining} tokens
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        
        {/* Pending Requests */}
        <div className="glass p-8 mb-6 animate-slide-up">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Pending Requests</h2>
            </div>
            {pendingRequests.length > 0 && (
              <div className="px-4 py-2 bg-yellow-50 text-yellow-700 rounded-lg font-semibold text-sm">
                {pendingRequests.length} {pendingRequests.length === 1 ? 'Request' : 'Requests'}
              </div>
            )}
          </div>
          {pendingRequests.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-500 text-lg font-medium">All caught up!</p>
              <p className="text-gray-400 text-sm mt-2">No pending requests to review</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingRequests.map(request => {
                const employee = employees.find(emp => emp.id === request.employeeId);
                return (
                  <RequestCard
                    key={request.id}
                    request={request}
                    employee={employee}
                    employees={employees}
                    showActions={true}
                    onApprove={request.status === 'pending' ? handleApprove : undefined}
                    onReject={request.status === 'pending' ? handleReject : undefined}
                    onApproveCancellation={request.status === 'cancellation_requested' ? handleApproveCancellation : undefined}
                    onRejectCancellation={request.status === 'cancellation_requested' ? handleRejectCancellation : undefined}
                    onView={(req) => setSelectedRequest(req)}
                  />
                );
              })}
            </div>
          )}
        </div>
        
          </>
        )}
        
        {/* Employees Tab */}
        {activeTab === 'employees' && (
          <div className="glass p-8 animate-slide-up">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Team Members</h2>
              </div>
              <button
                onClick={() => {
                  setEditingEmployee(null);
                  setPasswordResetEmployee(null);
                  setShowEmployeeForm(!showEmployeeForm);
                }}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 ${
                  showEmployeeForm
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-primary-500 text-white shadow-lg hover:shadow-xl hover:bg-primary-600'
                }`}
              >
                {showEmployeeForm ? (
                  <span className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Cancel</span>
                  </span>
                ) : (
                  <span className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Add Employee</span>
                  </span>
                )}
              </button>
            </div>
            
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-primary-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Center/Team
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Tokens
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {teamMembers.map(member => (
                    <tr key={member.id} className="hover:bg-primary-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                            {member.name?.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm font-semibold text-gray-900">{member.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {member.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {member.centerId ? (
                          <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg font-medium">
                            {centers.find(c => c.id === member.centerId)?.name || 'Unknown Center'}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">No center</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-700">
                        <span className="px-3 py-1 bg-primary-50 text-primary-700 rounded-lg">
                          {member.tokensUsed || 0} / {member.yearlyTokens || 12}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => {
                              setEditingEmployee(member);
                              setPasswordResetEmployee(null);
                              setShowEmployeeForm(true);
                            }}
                            className="px-4 py-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-all duration-200 font-semibold"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              setPasswordResetEmployee(member);
                              setEditingEmployee(null);
                              setShowEmployeeForm(true);
                            }}
                            className="px-4 py-2 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 rounded-lg transition-all duration-200 font-semibold"
                          >
                            Reset Password
                          </button>
                          <button
                            onClick={() => handleDeleteEmployee(member.id)}
                            className="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 font-semibold"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {teamMembers.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-lg font-medium">No team members yet</p>
                  <p className="text-gray-400 text-sm mt-2">Click "Add Employee" to add your first team member</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <>
            {/* Pending Requests */}
            <div className="glass p-8 mb-6 animate-slide-up">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800">Pending Requests</h2>
                </div>
                {pendingRequests.length > 0 && (
                  <div className="px-4 py-2 bg-yellow-50 text-yellow-700 rounded-lg font-semibold text-sm">
                    {pendingRequests.length} {pendingRequests.length === 1 ? 'Request' : 'Requests'}
                  </div>
                )}
              </div>
              {pendingRequests.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-lg font-medium">All caught up!</p>
                  <p className="text-gray-400 text-sm mt-2">No pending requests to review</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingRequests.map(request => {
                    const employee = employees.find(emp => emp.id === request.employeeId);
                    return (
                      <RequestCard
                        key={request.id}
                        request={request}
                        employee={employee}
                        employees={employees}
                        showActions={true}
                        onApprove={request.status === 'pending' ? handleApprove : undefined}
                        onReject={request.status === 'pending' ? handleReject : undefined}
                        onApproveCancellation={request.status === 'cancellation_requested' ? handleApproveCancellation : undefined}
                        onRejectCancellation={request.status === 'cancellation_requested' ? handleRejectCancellation : undefined}
                        onView={(req) => setSelectedRequest(req)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* All Team Requests */}
            <div className="glass p-8 animate-slide-up">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800">All Team Requests</h2>
                </div>
                <div className="px-4 py-2 bg-primary-50 text-primary-700 rounded-lg font-semibold text-sm">
                  {teamRequests.length} {teamRequests.length === 1 ? 'Request' : 'Requests'}
                </div>
              </div>
          <div className="space-y-4">
            {teamRequests.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-gray-500 text-lg font-medium">No team requests yet</p>
              </div>
            ) : (
              teamRequests
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map(request => {
                  const employee = employees.find(emp => emp.id === request.employeeId);
                  return (
                    <RequestCard
                      key={request.id}
                      request={request}
                      employee={employee}
                      employees={employees}
                      onView={(req) => setSelectedRequest(req)}
                    />
                  );
                })
            )}
          </div>
        </div>
          </>
        )}
        
        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="glass p-8 mb-6 animate-slide-up">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Team Calendar</h2>
            </div>
            <CalendarView 
              requests={teamRequests} 
              employees={employees} 
              centers={centers}
              settings={settings}
            />
          </div>
        )}
        
        {/* Request Details Modal */}
        {selectedRequest && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100">
              <div className="sticky top-0 bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4 flex items-center justify-between border-b border-primary-400">
                <h2 className="text-2xl font-bold text-white">Request Details</h2>
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="text-white hover:bg-primary-700 rounded-lg p-2 transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Employee Info */}
                <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="w-16 h-16 bg-primary-500 rounded-xl flex items-center justify-center text-white text-2xl font-bold shadow-md">
                    {employees.find(emp => emp.id === selectedRequest.employeeId)?.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">
                      {employees.find(emp => emp.id === selectedRequest.employeeId)?.name || 'Unknown Employee'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {employees.find(emp => emp.id === selectedRequest.employeeId)?.email || ''}
                    </p>
                  </div>
                </div>
                
                {/* Manager Info */}
                {(() => {
                  const employee = employees.find(emp => emp.id === selectedRequest.employeeId);
                  const manager = employee?.managerId ? employees.find(emp => emp.id === employee.managerId) : null;
                  
                  return (
                    <div className="flex items-center space-x-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <div className="flex-shrink-0">
                        <svg className="w-12 h-12 text-blue-600 bg-blue-100 rounded-lg p-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs uppercase tracking-wider text-blue-600 font-semibold mb-1">Request Raised To</p>
                        {manager ? (
                          <div>
                            <h3 className="text-lg font-bold text-blue-900">{manager.name}</h3>
                            <p className="text-sm text-blue-700">{manager.email}</p>
                          </div>
                        ) : (
                          <p className="text-sm text-blue-600 italic">No manager assigned</p>
                        )}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 border border-gray-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">Status</p>
                    <p className={`text-lg font-bold px-3 py-1 rounded-lg inline-block ${
                      selectedRequest.status === 'approved' ? 'bg-green-100 text-green-800' :
                      selectedRequest.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      selectedRequest.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      selectedRequest.status === 'cancellation_requested' ? 'bg-orange-100 text-orange-800' :
                      selectedRequest.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedRequest.status.replace(/_/g, ' ').toUpperCase()}
                    </p>
                  </div>
                  <div className="p-4 border border-gray-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">Duration</p>
                    <p className="text-lg font-bold text-primary-600">
                      {selectedRequest.days || 1} {selectedRequest.days === 1 ? 'day' : 'days'}
                    </p>
                  </div>
                </div>
                
                {/* Dates */}
                <div className="p-4 border border-gray-200 rounded-xl">
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">Dates</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 font-medium">Start Date:</span>
                      <span className="text-gray-800 font-semibold">{formatDate(selectedRequest.startDate)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 font-medium">End Date:</span>
                      <span className="text-gray-800 font-semibold">{formatDate(selectedRequest.endDate)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Reason */}
                {selectedRequest.reason && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-blue-600 font-semibold mb-2">Reason</p>
                    <p className="text-gray-800">{selectedRequest.reason}</p>
                  </div>
                )}
                
                {/* Rejection Reason */}
                {selectedRequest.status === 'rejected' && selectedRequest.rejectionReason && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-red-600 font-semibold mb-2">Rejection Reason</p>
                    <p className="text-gray-800">{selectedRequest.rejectionReason}</p>
                  </div>
                )}
                
                {/* Cancellation Reason */}
                {selectedRequest.cancellationReason && (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-orange-600 font-semibold mb-2">Cancellation Reason</p>
                    <p className="text-gray-800">{selectedRequest.cancellationReason}</p>
                  </div>
                )}
                
                {/* Review Info */}
                {selectedRequest.reviewedAt && (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Reviewed</p>
                    <p className="text-gray-800">
                      <span className="font-semibold">{formatDate(selectedRequest.reviewedAt)}</span> by {employees.find(emp => emp.id === selectedRequest.reviewedBy)?.name || selectedRequest.reviewedBy || 'Manager'}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end">
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="px-6 py-2.5 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Employee Form Modal */}
        {showEmployeeForm && !passwordResetEmployee && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4 flex items-center justify-between border-b border-primary-400">
                <h2 className="text-2xl font-bold text-white">
                  {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
                </h2>
                <button
                  onClick={() => {
                    setShowEmployeeForm(false);
                    setEditingEmployee(null);
                  }}
                  className="text-white hover:bg-primary-700 rounded-lg p-2 transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6">
                <EmployeeForm
                  employee={editingEmployee}
                  employees={teamMembers}
                  centers={centers}
                  onSubmit={editingEmployee ? handleUpdateEmployee : handleCreateEmployee}
                  onCancel={() => {
                    setShowEmployeeForm(false);
                    setEditingEmployee(null);
                  }}
                  restrictRole={true}
                  hideManagerAndCenter={true}
                  managerCenterId={employees.find(emp => emp.id === user?.id)?.centerId}
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Password Reset Modal */}
        {passwordResetEmployee && showEmployeeForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="sticky top-0 bg-gradient-to-r from-yellow-500 to-amber-500 px-6 py-4 flex items-center justify-between border-b border-yellow-400">
                <h2 className="text-2xl font-bold text-white">
                  Reset Password
                </h2>
                <button
                  onClick={() => {
                    setPasswordResetEmployee(null);
                    setShowEmployeeForm(false);
                  }}
                  className="text-white hover:bg-yellow-600 rounded-lg p-2 transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6">
                <div className="mb-4">
                  <p className="text-gray-700">
                    Resetting password for: <span className="font-bold text-gray-900">{passwordResetEmployee.name}</span>
                  </p>
                  <p className="text-sm text-gray-500 mt-1">{passwordResetEmployee.email}</p>
                </div>
                
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const password = e.target.newPassword.value;
                    if (!password || password.length < 6) {
                      alert('Password must be at least 6 characters long');
                      return;
                    }
                    setProcessing(true);
                    try {
                      await employeeService.resetPassword(passwordResetEmployee.id, password);
                      await auditService.logAudit({
                        userId: user.id,
                        userRole: user.role,
                        action: 'reset',
                        entityType: 'password',
                        entityId: passwordResetEmployee.id,
                        details: { employeeEmail: passwordResetEmployee.email },
                      });
                      await refreshAll();
                      setPasswordResetEmployee(null);
                      setShowEmployeeForm(false);
                      alert('Password reset successfully!');
                    } catch (error) {
                      alert('Failed to reset password: ' + error.message);
                    } finally {
                      setProcessing(false);
                    }
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                      New Password *
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      placeholder="Enter new password (min 6 characters)"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 bg-gray-50 focus:bg-white"
                      required
                      minLength="6"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={processing}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-200 disabled:opacity-50"
                    >
                      {processing ? 'Resetting...' : 'Reset Password'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPasswordResetEmployee(null);
                        setShowEmployeeForm(false);
                      }}
                      className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all duration-200"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerDashboard;

